import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import { AccommodationTip } from '@/scopes/search/search/infrastructure/api/types'
import { mqMin, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { IconButton } from '@sunwebgroup/ui-components/uds'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { styled } from 'styled-components'
import AccommodationCard from './AccommodationCard'

export interface AccommodationCardProps extends AccommodationTip {
  $orientation?: 'top' | 'bottom'
  slideIndex?: number
  participants?: RoomsParticipantDistribution
}

interface AccommodationTipsSliderProps {
  items: AccommodationCardProps[]
  participants?: RoomsParticipantDistribution
}

const StyledLeftButton = styled(IconButton)`
  left: 33px;
`

const StyledRightButton = styled(IconButton)`
  right: 33px;
`

const Container = styled.section`
  position: relative;
  ${StyledLeftButton}, ${StyledRightButton} {
    display: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
  }

  @media (hover: hover) {
    &:hover {
      ${StyledLeftButton}, ${StyledRightButton} {
        display: inline-flex;
      }
    }
  }
`

const Slider = styled.div`
  overflow: hidden;
  border-radius: ${tokPx('semantics_cards_corner_radius')};
`

const SliderContainer = styled.div`
  display: flex;
`

const Slide = styled.div`
  flex: 0 0 100%;
`

const Dots = styled.div`
  position: absolute;
  bottom: 16px;
  width: 100%;
  display: flex;
  justify-content: center;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    bottom: 20px;
  }
`

const Dot = styled.div<{ $active: boolean }>`
  width: ${space('primitives_space_8')};
  height: ${space('primitives_space_8')};
  border-radius: 50%;
  background-color: ${tok('primitives_color_neutral_0')};
  opacity: ${props => (props.$active ? 1 : 0.56)};
  margin: 0 4px;
`

export default function AccommodationTipsSlider({ items, participants }: AccommodationTipsSliderProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)

  function navigate(direction: 'previous' | 'next') {
    if (direction === 'previous') {
      emblaApi?.scrollPrev()
    }
    else {
      emblaApi?.scrollNext()
    }
  }

  const onSelect = useCallback(() => {
    if (emblaApi !== undefined) {
      const index = emblaApi.selectedScrollSnap()
      setSelectedIndex(index)
    }
  }, [emblaApi])

  useEffect(() => {
    setScrollSnaps(emblaApi?.scrollSnapList() ?? [])
    emblaApi?.on('select', onSelect)

    return () => {
      emblaApi?.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  return (
    <Container data-qa="accommodation-tips">
      <Slider ref={emblaRef}>
        <SliderContainer>
          {items.map((item, index) => (
            <Slide key={item.id}>
              <AccommodationCard
                arrivalDate={item.arrivalDate}
                departureDate={item.departureDate}
                acmInformation={item.acmInformation}
                url={item.url}
                id={item.id}
                images={item.images}
                locations={item.locations}
                mealPlan={item.mealPlan}
                name={item.name}
                occupancy={item.occupancy}
                price={item.price}
                rating={item.rating}
                slideIndex={index}
                userRating={item.userRating}
                userRatingText={item.userRatingText}
                outboundDepartureAirportName={item.outboundDepartureAirportName}
                participants={participants}
              />
            </Slide>
          ))}
        </SliderContainer>
      </Slider>
      {items.length > 1 && (
        <>
          <StyledLeftButton variant="overlay-neutral" size="S" glyph="ChevronLeft" onClick={() => navigate('previous')} />
          <StyledRightButton variant="overlay-neutral" size="S" glyph="ChevronRight" onClick={() => navigate('next')} />
        </>
      )}
      {scrollSnaps.length > 0 && (
        <Dots>
          {scrollSnaps.map((_, index) => (
            <Dot $active={index === selectedIndex} key={index} />
          ))}
        </Dots>
      )}
    </Container>
  )
}
